<template>
  <section class="containerAddPeopleInputGeneric" :class="inputType">
    <h2 class="containerAddPeopleInputGeneric__label">PEOPLE</h2>
    <section class="containerAddPeopleInputGeneric__input">
      <div class="containerAddPeopleInputGeneric__input--emails customScrollbar"
        :class="{ input__hasError: error, input__areSuggestions: areSuggestions, input__hasScrollbar: hasScrollbar }"
        @click.stop="focusInput"
        ref="formContentRef">
        <button
          v-if="areSuggestions"
          @mousedown="clearInputValue"
          class="cancelSuggestions">
          <span class="grey-icon-clear"></span>
        </button>
        <section class="pillContent" v-for="(email, index) in emails"
          :class="pillClass(email)"
          :key="`${email}-${index}`">
          <div class="pillContent__deleteButton" @click.stop="removeEmail(index)">
            <span class="icon-grey-close"></span>
          </div>
          <div class="pillContent__email">
            {{email}}
          </div>
        </section>
        <input type="text" class="emailInput" ref="emailInputRef"
          :value="emailInput"
          :placeholder="inputPlaceholder"
          @input="setInputValue"
          @blur="onEmailInputBlur(emailInput)"
          @keyup="inputKeyup"
          @keydown="inputKeydown"
          @paste.prevent="inputPaste">
      </div>
      <peopleSuggestionsGeneric
        ref="suggestionsRef"
        @onSelectItem="addSuggestion"
        :selectedMails="emails"
        :searchParameter="emailInput"
        :hideUserLogged="true"
        :maxSuggestions="inputType === 'share' ? 6 : 3"
        :userLogged="userLogged">
      </peopleSuggestionsGeneric>
      <span class="containerAddPeopleInputGeneric__input--error" v-if="error" v-html="error"></span>
    </section>
  </section>
</template>

<script>
import { ref, computed, nextTick, watch, onMounted } from 'vue'
import peopleSuggestionsGeneric from '../peopleSuggestionsGeneric/peopleSuggestionsGeneric'
import messagesEmails from './addPeopleInputGeneric.json'
import { iOSVersion, isMobile } from '../../../utils'

export default {
  name: 'addPeopleInputGeneric',
  components: {
    peopleSuggestionsGeneric
  },
  props: {
    validateEmails: {
      type: Boolean,
      default: false
    },
    initialEmails: {
      type: Array,
      default: () => []
    },
    inputType: {
      type: String,
      default: 'share'
    },
    failedEmails: {
      type: Array,
      default: () => []
    },
    userLogged: {
      type: String,
      default: ''
    }
  },
  setup (props, { emit }) {
    const emails = ref(props.initialEmails)
    const messages = messagesEmails
    const error = ref('')
    const failedEmails = ref(props.failedEmails)
    const emailInput = ref('')
    const emailInputRef = ref(null)
    const suggestionsRef = ref(null)
    const formContentRef = ref(null)
    const hasScrollbar = ref(false)

    /**
     * @description Emits list of emails when it changes
     */
    watch(() => emails.value.length, () => {
      emit('listUpdated', emails.value)
    })

    /**
     * @description Checks if emails are valid when validateEmails is true
     */
    watch(() => props.validateEmails, (validateEmails) => {
      if (validateEmails) checkEmails()
    })

    /**
     * @description Sets failed emails
     */
    watch(() => props.failedEmails, (failed) => {
      failedEmails.value = failed || []
    })

    /**
     * @description Retrieves the correct placeholder for the input depending on its state
     */
    const inputPlaceholder = computed(() => {
      return emails.value.length ? 'Add more people…' : 'Enter email addresses…'
    })

    /**
     * @description Retrieves if there are any visible suggestion
     */
    const areSuggestions = computed(() => {
      return suggestionsRef.value ? suggestionsRef.value.suggestions.length > 0 : false
    })

    onMounted(() => {
      filterUserLogged()
      scrollToBottom()
      checkScrollbar()
    })

    /**
     * @description Make the email input gain focus
     */
    function focusInput () {
      emailInputRef.value.focus()
    }

    /**
     * @description Gets the class for the pill depending on the email format and if it's a valid email
     */
    function pillClass (email) {
      if (failedEmails.value.indexOf(email) >= 0) return 'invalid'
      /* eslint-disable */
      const regex = /^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,5})$/
      /* eslint-enable */
      return regex.test(email) ? 'valid' : 'wrongFormat'
    }

    /**
     * @description Remove one of the emails from the list
     * @param index Index of the email to be remove
     */
    function removeEmail (index) {
      const invalidIndex = failedEmails.value.indexOf(emails.value[index])
      if (invalidIndex >= 0) failedEmails.value.splice(invalidIndex, 1)
      error.value = ''
      emails.value.splice(index, 1)
      nextTick(() => {
        checkScrollbar()
      })
    }

    /**
     * @description Check the state of the input to see if more pills need to be added
     * @param event User generated event
     */
    function inputKeyup (event) {
      if (isMobile && (/\.\s/).test(emailInput.value)) emailInput.value = emailInput.value.trim()
      if ((/[,;]/).test(emailInput.value.substr(emailInput.value.length - 1)) || event.key === 'Enter') {
        const newEmail = emailInput.value.split(/[,;]/)[0].trim()
        addEmailPill(newEmail)
      }
    }

    /**
     * @description Add selected suggestion as pill
     * @param email mail to add as string
     */
    function addSuggestion (email) {
      addEmailPill(email)
      focusInput()
    }

    /**
     * @description Clear email input
     */
    function clearInputValue () {
      emailInput.value = ''
    }

    /**
     * @description Handle Blur Event
     * @param emailInput mail to add as string
     */
    function onEmailInputBlur (emailInput) {
      nextTick(() => {
        addEmailPill(emailInput)
      })
    }

    /**
     * @description Check for backspace key to delete the last email
     * @param event User generated event
     */
    function inputKeydown (event) {
      error.value = ''
      if (event.key === 'Backspace' && emailInput.value === '') {
        emails.value.pop()
      }
    }

    /**
     * @description Check for pasted emails to turn them into pills
     * @param event User generated event
     */
    function inputPaste (event) {
      const clipData = event.clipboardData || window.clipboardData
      clipData.getData('Text').split(/[,;\s]/).forEach(email => {
        addEmailPill(email)
      })
    }

    /**
     * @description Add a new email pill checking it's not empty nor duplicated
     * @param newEmail New email to be added as a pill
     */
    function addEmailPill (newEmail) {
      if (newEmail !== '') {
        const duplicate = emails.value.indexOf(newEmail)
        if (duplicate >= 0) emails.value.splice(duplicate, 1)
        emails.value.push(newEmail)
        nextTick(() => {
          scrollToBottom()
          checkScrollbar()
        })
      }
      clearInputValue()
    }

    /**
     * @description Filter emails with wrong format
     */
    function wrongFormatEmails () {
      return emails.value.filter(email => pillClass(email) === 'wrongFormat')
    }

    /**
     * @description Checks if emails are valid
     */
    function checkEmails () {
      if (emails.value.length === 0 && props.inputType === 'share') {
        setError('empty')
        return
      }
      const wrongEmails = wrongFormatEmails()
      if (wrongEmails.length > 0) {
        emit('emailsValidated', 'failed')
        setError(`${wrongEmails.length === 1 ? 'single' : 'multiple'}WrongFormat`, wrongEmails.length === 1 ? wrongEmails[0] : '')
      } else {
        emit('emailsValidated', 'success')
      }
    }

    /**
     * @description Sets error messages
     * @param errorType The error to show
     * @param wrongEmail The email with error
     */
    function setError (errorType, wrongEmail = '') {
      error.value = wrongEmail ? messages.error[errorType].replace('${}', wrongEmail) : messages.error[errorType]
    }

    /**
     * @description Scrolls emails container to the bottom
     */
    function scrollToBottom () {
      formContentRef.value.scrollTo(0, formContentRef.value.scrollHeight)
    }

    /**
     * @description Checks if scrollbar of emails container is visible
     */
    function checkScrollbar () {
      hasScrollbar.value = formContentRef.value.scrollHeight > formContentRef.value.offsetHeight && !(iOSVersion() >= 13)
    }

    /**
     * @description Hide user logged from emails list
     */
    function filterUserLogged () {
      emails.value = emails.value.filter(email => props.userLogged !== email)
    }

    /**
     * @description Updates the input value when it changes
     * @param event DOM event
     */
    function setInputValue (event) {
      emailInput.value = event.target.value
    }

    return {
      messages,
      inputPlaceholder,
      focusInput,
      removeEmail,
      inputKeyup,
      inputKeydown,
      inputPaste,
      checkEmails,
      emails,
      emailInput,
      error,
      emailInputRef,
      pillClass,
      addEmailPill,
      addSuggestion,
      onEmailInputBlur,
      suggestionsRef,
      areSuggestions,
      clearInputValue,
      formContentRef,
      setError,
      hasScrollbar,
      setInputValue
    }
  }
}
</script>
